import axios, { AxiosInstance } from 'axios';
import type { Service, Barber } from '../types';
import type { Product, Order, OrderItem } from '../types/shop';

// Create a variable to store the notification function
// This will be set by the setupNotifications function
let showNotificationFn: ((message: string, type: 'success' | 'error' | 'info' | 'warning', duration?: number) => void) | null = null;

// Function to setup notifications - will be called from App.tsx
export const setupNotifications = (
  showNotification: (message: string, type: 'success' | 'error' | 'info' | 'warning', duration?: number) => void
) => {
  showNotificationFn = showNotification;
};

// Default timeout for API requests (20 seconds)
const DEFAULT_TIMEOUT = 20000;

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
// const API_URL = 'http://192.168.0.136:8000';

const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: DEFAULT_TIMEOUT, // Set default timeout for all requests
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    console.log('Added Authorization header:', `Bearer ${token.substring(0, 10)}...`);
  } else {
    console.log('No token found in localStorage');
  }
  return config;
});

// Add a response interceptor to handle errors and show notifications
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      console.error('API Error Response:', {
        status: error.response.status,
        data: error.response.data,
        headers: error.response.headers,
        url: error.config.url,
        method: error.config.method,
      });
      
      // Extract error message
      let errorMessage = 'An error occurred';
      
      if (error.response.data) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.detail && typeof error.response.data.detail === 'object') {
          // Handle case where detail is an object
          errorMessage = 'Validation error occurred';
          try {
            // Try to convert the object to a string representation
            errorMessage = JSON.stringify(error.response.data.detail);
          } catch (e) {
            console.error('Failed to stringify error detail:', e);
          }
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        }
      }
      
      // Show notification if the function is available
      if (showNotificationFn) {
        // Use longer duration for errors (8 seconds)
        showNotificationFn(errorMessage, 'error', 8000);
      }
    } else if (error.request) {
      console.error('API Error Request:', error.request);
      
      // Show network error notification
      if (showNotificationFn) {
        // Use longer duration for network errors (10 seconds)
        showNotificationFn('Network error. Please check your connection.', 'error', 10000);
      }
    } else {
      console.error('API Error:', error.message);
      
      // Show generic error notification
      if (showNotificationFn) {
        // Use standard duration for generic errors (5 seconds)
        showNotificationFn(`Error: ${error.message}`, 'error', 5000);
      }
    }
    return Promise.reject(error);
  }
);

// Create a public API instance without auth headers for guest operations
const publicApi: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: DEFAULT_TIMEOUT,
});

// Add the same response interceptor to handle errors
publicApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      console.error('API Error Response (Public):', {
        status: error.response.status,
        data: error.response.data,
        headers: error.response.headers,
        url: error.config.url,
        method: error.config.method,
      });
      
      // Extract error message
      let errorMessage = 'An error occurred';
      
      if (error.response.data) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.detail && typeof error.response.data.detail === 'object') {
          // Handle case where detail is an object
          errorMessage = 'Validation error occurred';
          try {
            // Try to convert the object to a string representation
            errorMessage = JSON.stringify(error.response.data.detail);
          } catch (e) {
            console.error('Failed to stringify error detail:', e);
          }
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        }
      }
      
      // Show notification if the function is available
      if (showNotificationFn) {
        // Use longer duration for errors (8 seconds)
        showNotificationFn(errorMessage, 'error', 8000);
      }
    } else if (error.request) {
      console.error('API Error Request (Public):', error.request);
      
      // Show network error notification
      if (showNotificationFn) {
        // Use longer duration for network errors (10 seconds)
        showNotificationFn('Network error. Please check your connection.', 'error', 10000);
      }
    } else {
      console.error('API Error (Public):', error.message);
      
      // Show generic error notification
      if (showNotificationFn) {
        // Use standard duration for generic errors (5 seconds)
        showNotificationFn(`Error: ${error.message}`, 'error', 5000);
      }
    }
    return Promise.reject(error);
  }
);

interface LoginResponse {
  access_token: string;
  token_type: string;
  user_id: string;
}

interface ClientData {
  name: string;
  email: string;
  phone: string;
  password: string;
}

interface WorkingHourDay {
  day_of_week: number;
  start_time: string;
  end_time: string;
  is_working: boolean;
}

export interface VacationDayResponse {
  id: number;
  start_date: string;
  end_date: string;
  description?: string;
}

export interface VacationDayRequest {
  start_date: string;
  end_date: string;
  description?: string;
}

export interface ServiceData {
  name: string;
  description: string;
  duration: number;
  price: number;
  imageUrl?: string;
}

export const login = async (
  email: string,
  password: string,
  userType: 'client' | 'barber'
): Promise<LoginResponse> => {
  const formData = new FormData();
  formData.append('username', email);
  formData.append('password', password);
  formData.append('grant_type', 'password');
  formData.append('scope', userType);
  
  try {
    const response = await api.post<LoginResponse>('/token', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

export const registerClient = async (clientData: ClientData) => {
  const response = await api.post('/client/register', clientData);
  return response.data;
};

export const getServices = async () => {
  const response = await api.get<Service[]>('/client/services');
  return response.data;
};

export const addService = async (serviceData: ServiceData) => {
  const response = await api.post('/barber/services', serviceData);
  return response.data;
};

export const setWorkingHours = async (workingHours: WorkingHourDay[]) => {
  const response = await api.post('/barber/working-hours', {
    working_hours: workingHours,
  });
  return response.data;
};

export const getWorkingHours = async (barberId: number) => {
  const response = await api.get<WorkingHourDay[]>(`/barber/working-hours/${barberId}`);
  return response.data;
};

export const getAvailableSlots = async (
  barberId: number, 
  serviceId: number, 
  startDate: string, 
  endDate: string
): Promise<Array<{ date: string; time: string }>> => {
  const response = await api.get(`/client/month-slots/${barberId}`, {
    params: { 
      service_id: serviceId,
      start_date: startDate,
      end_date: endDate
    }
  });
  return response.data;
};

export const bookAppointment = async (data: {
  service_id: number;
  barber_id: number;
  date: string;
  time: string;
}) => {
  const response = await api.post('/client/appointments', data);
  return response.data;
};

export const getBarberAppointments = async (date?: string) => {
  const response = await api.get('/barber/appointments', {
    params: { date },
  });
  return response.data;
};

export const getClientAppointments = async () => {
  const response = await api.get('/client/appointments');
  return response.data;
};

export const updateAppointmentStatus = async (
  appointmentId: number,
  status: 'completed' | 'cancelled'
) => {
  const response = await api.patch(`/barber/appointments/${appointmentId}`, {
    status: status
  });
  return response.data;
};

export const rescheduleAppointment = async (
  appointmentId: number,
  date: string,
  time: string
) => {
  try {
    const response = await api.patch(`/barber/appointments/${appointmentId}/reschedule`, {
      date,
      time
    });
    
    // Show success notification
    if (showNotificationFn) {
      // Use shorter duration for success messages (3 seconds)
      showNotificationFn('Appointment rescheduled successfully', 'success', 3000);
    }
    
    return response.data;
  } catch (error: any) {
    // Error is already handled by the interceptor, but we can add specific handling here
    if (error.response && error.response.status === 400) {
      // Extract more specific error message for 400 errors
      const errorMessage = error.response.data.detail || 'Invalid request';
      
      // Check for specific error messages and provide more user-friendly versions
      if (errorMessage.includes('not a working day')) {
        if (showNotificationFn) {
          showNotificationFn(`Cannot reschedule to this day: ${errorMessage}`, 'error', 8000);
        }
      } else if (errorMessage.includes('outside working hours')) {
        if (showNotificationFn) {
          showNotificationFn('The requested time is outside working hours', 'error', 8000);
        }
      } else if (errorMessage.includes('overlaps with another appointment')) {
        if (showNotificationFn) {
          showNotificationFn('This time slot is already booked', 'error', 8000);
        }
      }
    }
    
    // Re-throw the error to allow the component to handle it if needed
    throw error;
  }
};

export const cancelAppointment = async (appointmentId: number) => {
  const response = await api.delete(`/client/appointments/${appointmentId}`);
  return response.data;
};

export const getBarbers = async () => {
  const response = await api.get<Barber[]>('/client/barbers');
  return response.data;
};

export const createVacationDays = async (vacationDay: VacationDayRequest): Promise<{ message: string }> => {
  const response = await api.post<{ message: string }>('/barber/vacation', vacationDay);
  return response.data;
};

export const getVacationDays = async (startDate: string, endDate: string): Promise<VacationDayResponse[]> => {
  const response = await api.get<VacationDayResponse[]>('/barber/vacation', {
    params: { start_date: startDate, end_date: endDate },
  });
  return response.data;
};

export const deleteVacationDay = async (vacationId: number) => {
  const response = await api.delete(`/barber/vacation/${vacationId}`);
  return response.data;
};

export const bookAppointmentAsGuest = async (appointmentData: any) => {
  try {
    const response = await api.post('/client/guest-booking', appointmentData);
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

export const getProducts = async (barberId: number) => {
  const response = await publicApi.get<Product[]>(`/client/products/${barberId}`);
  return response.data;
};

export const createOrder = async (orderData: {
  items: Array<{ product_id: number; quantity: number; }>
}) => {
  const response = await api.post('/client/orders', orderData);
  return response.data;
};

export const getOrders = async () => {
  const response = await api.get('/client/orders');
  return response.data;
};

export const getBarberOrders = async () => {
  const response = await api.get<Order[]>('/barber/orders');
  return response.data;
};

export const updateShippingCost = async (barberId: number, cost: number) => {
  const response = await api.post('/barber/shipping-cost', { 
    cost,
    barber_id: barberId 
  });
  return response.data;
};

export const getShippingCost = async (barberId: number) => {
  try {
    const response = await publicApi.get('/barber/shipping-cost', {
      params: { barber_id: barberId }
    });
    return response.data.cost || 0;
  } catch (error: any) {
    console.warn('Failed to get shipping cost, using default value:', error);
    return 0; // Default shipping cost if the API call fails
  }
};

export const createGuestOrder = async (orderData: {
  items: Array<{ product_id: number; quantity: number; }>,
  customer_info: {
    name: string;
    email: string;
    phone: string;
    address: string;
  },
  barberId: number
}) => {
  // Restructure the data to match the API's expected format
  const formattedData = {
    items: orderData.items,
    client_name: orderData.customer_info.name,
    client_email: orderData.customer_info.email,
    client_phone: orderData.customer_info.phone,
    client_address: orderData.customer_info.address,
    barber_id: orderData.barberId
  };
  
  // Use publicApi instead of api to avoid sending auth headers
  const response = await publicApi.post('/client/guest-orders', formattedData);
  return response.data;
};

export const getBarberProducts = async () => {
  const response = await api.get<Product[]>('/barber/products');
  return response.data;
};

export const createProduct = async (product: Partial<Product>) => {
  const response = await api.post<Product>('/barber/products', product);
  return response.data;
};

export const updateProduct = async (id: number, product: Partial<Product>) => {
  const response = await api.put<Product>(`/barber/products/${id}`, product);
  return response.data;
};

export const getBarberById = async (barberId: number) => {
  const response = await api.get<Barber>(`/client/barbers/${barberId}`);
  return response.data;
};

export const updateService = async (id: number, serviceData: ServiceData) => {
  const response = await api.put(`/barber/services/${id}`, serviceData);
  return response.data;
};

export const getUserProfile = async () => {
  const response = await api.get('/user/profile');
  return response.data;
};

export const updateUserProfile = async (formData: FormData) => {
  const response = await api.put('/user/profile', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export default api; 